import React, { useState, useContext } from 'react';
import { motion } from 'framer-motion';
import { pageVariants, pageTransition } from '../../constants/GlobalStyles';
import AppContext from '../../contexts/app-context';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// Charts
import AnalysisPageLineChart from '../../components/Charts/line/AnalysisPageLineChart';
import StateChart from '../../components/Charts/pie/StateChart';
import Statistics from '../../components/Charts/statistics/Statistics';
import MostUsedDrugsPieChart from '../../components/Charts/pie/MostUsedDrugsPieChart';
import MostUsedIcdsPieChart from '../../components/Charts/pie/MostUsedIcdsPieChart';
import MostUsedCptsPieChart from '../../components/Charts/pie/MostUsedCptsPieChart';
import useInsuranceAnalysisData from './useInsuranceAnalysisData';
import FilterModal from '../../components/filter/FilterModal';
import HighetDrugSpend from '../../components/Charts/bar/HighetDrugSpend';
import EncounterResolution from '../../components/Charts/bar/EncounterResolution';
import ExpensiveDrugPrescribingProviders from '../../components/Charts/bar/ExpensiveDrugPrescribingProviders';
import ProvidersWithHighRejectionRates from '../../components/Charts/bar/ProvidersWithHighRejectionRates';
import PatientsWithExcessiveEncounterFrequency from '../../components/Charts/bar/PatientsWithExcessiveEncounterFrequency';
import IcdIssues from '../../components/Charts/pie/IcdIssues';
import IcdIssuesTable from '../../components/Tables/IcdIssuesTable';
import CptIssues from '../../components/Charts/pie/CptIssues';
import CptIssuesTable from '../../components/Tables/CptIssuesTable';
import DrugIssues from '../../components/Charts/pie/DrugIssues';
import DrugIssuesTable from '../../components/Tables/DrugIssuesTable';
import RefillTrend from '../../components/Charts/line/RefillTrend';

// Icons for the statistic cards
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SavingsIcon from '@mui/icons-material/Savings';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import GppBadIcon from '@mui/icons-material/GppBad';
import DoNotDisturbAltIcon from '@mui/icons-material/DoNotDisturbAlt';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PaidIcon from '@mui/icons-material/Paid';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TuneIcon from '@mui/icons-material/Tune';

import Divider from "@mui/material/Divider"
// import Scatter from '../../components/Charts/scatter/Scatter';
// import HeatMap from '../../components/Charts/heatMap/HeatMap';
import PendingRefillsTable from '../../components/Tables/PendingRefillsTable';
import PatientAbuseTable from '../../components/Tables/PatientAbuseTable';
import { statsTitles, departmentLabel, divisionLabel } from '../../components/config/variables';

const InsuranceAnalysisPage = () => {
  const appCtx = useContext(AppContext);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    department: '',
    division: '',
    doctor: '',
  });
  // used to pass to the filters modal to update the filters after delete a spesific filter
  const [newFiltersAfterDelete, setNewFiltersAfterDelete] = useState(undefined)

  // Apply filters -> build global query string for children
  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    const { fromDate, toDate, department, division, doctor } = newFilters;
    const params = [];
    if (fromDate) params.push(`from_date=${fromDate}`);
    if (toDate) params.push(`to_date=${toDate}`);
    if (department) params.push(`department=${department}`);
    if (division) params.push(`division=${division}`);
    if (doctor) params.push(`doctor=${doctor}`);
    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    appCtx.handleSetGlobalFilterQuery(queryString);
    setIsFilterModalOpen(false);
  };

  // Suffixes
  const symbol = {
    total_expenditure: '$',
    approved_ratio: '%',
    avg_cost: '$',
    money_saved: '$',
    total_cost_on_upcoming_refiles: '$'
  };

  const params = new URLSearchParams(appCtx.globalFilterQuery);

  const department  = params.get('department');
  const division  = params.get('division');
  const doctor = params.get('doctor');
  const from_date = params.get('from_date');
  const to_date = params.get('to_date');

  const deleteSpesificFilter = (filterKey) => {
    filters[filterKey] = "";
    handleApplyFilters(filters)
    setNewFiltersAfterDelete(filters)

};

  // Icons (passed per key to <Statistics />)
  const statisticIcons = {
    total_expenditure: (
      <AttachMoneyIcon fontSize="small" sx={{ color: '#fff' }} />
    ),
    total_encounters: (
      <EventAvailableIcon fontSize="small" sx={{ color: '#fff' }} />
    ),
    avg_cost: (
      <RequestQuoteIcon fontSize="small" sx={{ color: '#fff' }} />
    ),
    active_doctors: (
      <MedicalServicesIcon fontSize="small" sx={{ color: '#fff' }} />
    ),
    top_drug: (
      <CalendarTodayIcon fontSize="small" sx={{ color: '#fff' }} />
    ),
    money_saved: (
      <SavingsIcon fontSize="small" sx={{ color: '#fff' }} />
    ),
    rejected_icds: (
      <GppBadIcon fontSize="small" sx={{ color: '#fff' }} />
    ),
    rejected_cpts: (
      <ReportProblemIcon fontSize="small" sx={{ color: '#fff' }} />
    ),
    rejected_drugs: (
      <DoNotDisturbAltIcon fontSize="small" sx={{ color: '#fff' }} />
    ),
    approved_ratio: (
      <CheckCircleIcon fontSize="small" sx={{ color: '#fff' }} />
    ),
    total_patients: (
      <PeopleAltIcon fontSize="small" sx={{ color: '#fff' }} />
    ),
    total_patient_not_refile_yet: (
      <HourglassEmptyIcon fontSize="small" sx={{ color: '#fff' }} />
    ),
    total_cost_on_upcoming_refiles: (
      <PaidIcon fontSize="small" sx={{ color: '#fff' }} />
    ),
    total_patient_who_refile_this_month: (
      <EventRepeatIcon fontSize="small" sx={{ color: '#fff' }} />
    ),
    expensive_drugs_prescribed: (
      <LocalPharmacyIcon fontSize="small" sx={{ color: '#fff' }} />
    ),
    patient_abusing_system: (
      <WarningAmberIcon fontSize="small" sx={{ color: '#fff' }} />
    ),
    high_rejection_providers: (
      <PersonSearchIcon fontSize="small" sx={{ color: '#fff' }} />
    ),
    excessive_encounters: (
      <TrendingUpIcon fontSize="small" sx={{ color: '#fff' }} />
    ),
  };

  const theme = ['Costs', 'Compliance Check', 'Refill Patterns', 'Pattern Detection']
  const [selectedTheme, setSelectedTheme] = useState('Costs')

  const handleSelectTheme = (theme) => {
    setSelectedTheme(theme)
  }

  const CostsTheme = () => {
  // Fetch stats from API
  const { statsData: medCostData } = useInsuranceAnalysisData('med_cost');

    return (
      <>
        <Box sx={{ position: 'relative' }}>
          <Statistics
            data={medCostData}
            cards={['total_expenditure', 'avg_cost', 'money_saved', 'top_drug']}
            titles={statsTitles}
            icons={statisticIcons}
            symbol={symbol}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            border: '1px solid #d9d9d9',
            borderRadius: '10px',
            alignItems: 'center',
            padding: '15px',
            minHeight: '360px'
          }}
        >
          <Box sx={{ width: '70%', position: 'relative' }}>
            <Typography sx={{ position: 'absolute', top: '-10px', left: '10px', fontWeight: 500, fontSize: '18px' }}>
              Claims Trend
            </Typography>
            <Typography variant="h6" sx={{ position: 'absolute', top: '10px', left: '10px', fontSize: '14px', textAlign: 'center', color: '#86878C' }}>
              Approved vs rejected claim costs over time
            </Typography>
            <AnalysisPageLineChart hideMenu={true}/>
          </Box>
          <Box sx={{ width: '30%', position: 'relative' }}>
            <Typography sx={{position: 'absolute', top: '-30px', fontWeight: 500, fontSize: '18px'}}>Encounter State</Typography>
            <StateChart />
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 2,
            border: '1px solid #d9d9d9',
            borderRadius: '10px',
            mt: 2,
            p: 2,
            marginBottom: '10px'
          }}
        >
          <Box sx={{ p: 2, borderRight: { md: '1px solid #eee' } }}>
            <Typography variant="h6" sx={{ mb: 2, fontSize: 20, fontWeight: 500, textAlign: 'center' }}>
              Top Drugs
            </Typography>
            <MostUsedDrugsPieChart allTime={true} chartId="drugs-chart-1" />
          </Box>

          <Box sx={{ p: 2, borderRight: { md: '1px solid #eee' } }}>
            <Typography variant="h6" sx={{ mb: 2, fontSize: 20, fontWeight: 500, textAlign: 'center' }}>
              Top Diagnosis
            </Typography>
            <MostUsedIcdsPieChart />
          </Box>

          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontSize: 20, fontWeight: 500, textAlign: 'center' }}>
              Top Procedures
            </Typography>
            <MostUsedCptsPieChart chartId="CPTs-chart" />
          </Box>
        </Box>

        <Box sx={{ border: "1px solid #dededeff", mb: 2, p: 2, borderRadius: '10px 10px 0 0' }}>
          <HighetDrugSpend />
        </Box>

        {/* <Box
          sx={{
            gap: 2,
            border: '1px solid #d9d9d9',
            borderRadius: '10px',
            mt: 2,
            marginBottom: '10px'
          }}>

            <HighetDrugSpend />
          </Box> */}

      </> 
    )
  }

  const ComplianceCheckTheme = () => {
  // Fetch stats from API
  const { statsData: ruleCompData } = useInsuranceAnalysisData('rule_comp');

    return (
      <Box>
        <Box sx={{ position: 'relative', marginBottom: '10px' }}>
          <Statistics
            data={ruleCompData}
            cards={['approved_ratio', 'rejected_icds', 'rejected_cpts', 'rejected_drugs']}
            titles={statsTitles}
            icons={statisticIcons}
            symbol={symbol}
          />
        </Box>
        <Box sx={{ border: "1px solid #dededeff", borderRadius: 2, marginBottom: '10px' }}>
          <EncounterResolution />
        </Box>

        {/* Drug issues table */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 2fr',
            gap: 2,
            border: '1px solid #d9d9d9',
            borderRadius: '10px',
            p: 2,
            marginBottom: '10px'
          }}
        >
          <Box sx={{ p: 2, borderRight: { md: '1px solid #eee' } }}>
            <Typography variant="h6" sx={{ fontSize: 20, fontWeight: 500, textAlign: 'center' }}>
              Drug Rejections
            </Typography>
            <Typography variant="h6" sx={{ mb: 2, fontSize: 14, textAlign: 'center', color: '#86878C' }}>
              Distribution of drug rejection reasons
            </Typography>
            <DrugIssues allTime={true} chartId="drugs-issues-chart-1" />
          </Box>

          <Box>
            <DrugIssuesTable/>
          </Box>
        </Box>

        {/* Cpt issues table */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 2fr',
          gap: 2,
          border: '1px solid #d9d9d9',
          borderRadius: '10px',
          p: 2,
          marginBottom: '10px'
        }}>
          <Box sx={{ p: 2, borderRight: { md: '1px solid #eee' } }}>
            <Typography variant="h6" sx={{fontSize: 20, fontWeight: 500, textAlign: 'center' }}>
              Procedure Rejections
            </Typography>
            <Typography variant="h6" sx={{ mb: 2, fontSize: 14, textAlign: 'center', color: '#86878C' }}>
              Distribution of CPT rejection reasons
            </Typography>
            <CptIssues chartId="CPTs-issues-chart" />
          </Box>

          <Box>
            <CptIssuesTable />
          </Box>
        </Box>

        {/* Icd issues table */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 2fr',
          gap: 2,
          border: '1px solid #d9d9d9',
          borderRadius: '10px',
          p: 2,
          marginBottom: '10px'
        }}>
          <Box sx={{ p: 2, borderRight: { md: '1px solid #eee' } }}>
            <Typography variant="h6" sx={{fontSize: 20, fontWeight: 500, textAlign: 'center' }}>
              Diagnosis Rejections
            </Typography>
            <Typography variant="h6" sx={{ mb: 2, fontSize: 14, textAlign: 'center', color: '#86878C' }}>
              Distribution of ICD rejection reasons
            </Typography>
            <IcdIssues />
          </Box>

          <Box>
            <IcdIssuesTable />
          </Box>
        </Box>
      </Box>
    )
  }

  const QualityAdherenceTheme = () => {
  // Fetch stats from API
  const { statsData: patientHealthData } = useInsuranceAnalysisData('patient_health');

    return (
      <Box>
        <Box sx={{ marginBottom: '10px' }}>
          <Statistics
            data={patientHealthData}
            cards={['total_patients', 'total_patient_not_refile_yet', 'total_cost_on_upcoming_refiles', 'total_patient_who_refile_this_month']}
            titles={statsTitles}
            icons={statisticIcons}
            symbol={symbol}
          />
        </Box>

        {/* <Box sx={{
          border: '1px solid #d9d9d9',
          borderRadius: '10px',
          marginBottom: '10px'
        }}>
          <HeatMap />
        </Box> */}

        <Box sx={{ p: 2, border: "1px solid #dededeff", borderRadius: 2, marginBottom: '10px' }}>
          <RefillTrend />
        </Box>

        <Box sx={{ border: "1px solid #ccc", mb: 2, borderRadius: '10px 10px 0 0' }}>
          <Box
            sx={{
              color: '#fff',
              backgroundColor: '#607D8B', // blue-gray tone
              padding: '8px 12px',
              borderRadius: '10px 10px 0 0',
            }}
          >
            <Typography sx={{ fontSize: '20px', fontWeight: '600', color: '#ffffff' }}>
              Refill Management
            </Typography>
            <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#E0E0E0' }}>
              Track medication refills across all stages
            </Typography>
          </Box>
          <Box sx={{ padding: '10px', backgroundColor: '#fafafa' }}>
            <PendingRefillsTable />
          </Box>
        </Box>

      </Box>
    )
  }

  const FowDetectionTheme = () => {
  // Fetch stats from API
  const { statsData: fawData } = useInsuranceAnalysisData('fwa');

    return (
      <Box>
        <Box sx={{ marginBottom: '10px' }}>
          <Statistics
            data={fawData}
            cards={['expensive_drugs_prescribed', 'patient_abusing_system', 'high_rejection_providers', 'excessive_encounters']}
            titles={statsTitles}
            icons={statisticIcons}
            symbol={symbol}
          />
        </Box>

        <Box sx={{ border: "1px solid #dededeff", mb: 2, p: 2, borderRadius: '10px 10px 0 0' }}>
          <ExpensiveDrugPrescribingProviders/>
        </Box>

        <Box sx={{ border: "1px solid #dededeff", mb: 2, p: 2, borderRadius: '10px 10px 0 0' }}>
          <ProvidersWithHighRejectionRates/>
        </Box>

        <Box sx={{ border: "1px solid #dededeff", mb: 2, p: 2, borderRadius: '10px 10px 0 0' }}>
          <PatientsWithExcessiveEncounterFrequency/>
        </Box>

        <Box sx={{ border: "1px solid #dededeff", mb: 2, p: 2, borderRadius: '10px 10px 0 0' }}>
          <Typography sx={{fontSize: '18px', fontWeight: '500', marginBottom: '2px'}}>Suspicious Activity</Typography>
          <Typography sx={{color: '#86878C', fontWeight: '500', marginBottom: '10px'}}>Patients with potential system abuse patterns</Typography>
          <PatientAbuseTable/>
        </Box>
      </Box>
    )
  }



  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={pageTransition}
    >
      <Box sx={{ mt: 2, mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'center',
          }}
        >
          {/* Configure Filters */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.8,
              px: 1.5,
              py: 0.7,
              borderRadius: '10px',
              bgcolor: 'primary.main',
              color: 'white',
              fontSize: 16,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': { opacity: 0.85 },
            }}
            onClick={() => setIsFilterModalOpen(true)}
          >
            <TuneIcon fontSize="small" />
            <Typography sx={{fontWeight: '500'}}>All Filters</Typography>
          </Box>

          {/* Active Filters */}
          {[
            { label: 'From Date', value: from_date, key: 'fromDate' },
            { label: 'To Date', value: to_date, key: 'toDate' },
            { label: 'Department', value: department && departmentLabel[department], key: 'department' },
            { label: 'Division', value: division && divisionLabel[division], key: 'division' },
            { label: 'Doctor', value: doctor, key: 'doctor' },
          ]
            .filter(f => f.value)
            .map(f => (
              <Box
                key={f.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1.3,
                  py: 0.5,
                  borderRadius: '10px',
                  bgcolor: 'background.default',
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  fontSize: 16,
                  transition: 'all 0.2s ease',
                  '&:hover': { boxShadow: '0 2px 6px rgba(0,0,0,0.1)' },
                }}
              >
                <Typography sx={{ fontWeight: 500 }}>
                  {f.label}: {f.value}
                </Typography>
                <HighlightOffIcon
                  onClick={() => deleteSpesificFilter(f.key)}
                  sx={{
                    cursor: 'pointer',
                    fontSize: 18,
                    color: 'text.secondary',
                    '&:hover': { color: 'error.main' },
                  }}
                />
              </Box>
            ))}
        </Box>
      </Box>

      <FilterModal
          open={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          onApply={handleApplyFilters}
          newFiltersAfterDelete={newFiltersAfterDelete}
          initialFilters={filters}
          fields={["date", "department", "division", "doctor"]}
        />

        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              p: 1,
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              width: 'fit-content',
              mx: 'auto',
              mb: 3,
              userSelect: 'none',
            }}
          >
            {theme.map((t, index) => (
              <React.Fragment key={index}>
                <Box
                  onClick={() => handleSelectTheme(t)}
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    backgroundColor:
                      selectedTheme === t ? 'primary.main' : 'transparent',
                    color: selectedTheme === t ? 'primary.contrastText' : 'text.primary',
                    '&:hover': {
                      backgroundColor:
                        selectedTheme === t ? 'primary.dark' : 'action.hover',
                    },
                  }}
                >
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{t}</Typography>
                </Box>
                {theme.length - 1 !== index && (
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                )}
              </React.Fragment>
            ))}
          </Box>

        {
          selectedTheme === 'Costs' ? (
            <Box>
              <CostsTheme />
            </Box>
          ) : selectedTheme === 'Compliance Check' ? (
            <ComplianceCheckTheme />
          ) : selectedTheme === 'Refill Patterns' ? (
            <QualityAdherenceTheme />
          ) : (
            <FowDetectionTheme />
          )
        }
      </>
    </motion.div>
  );
};

export default InsuranceAnalysisPage;